import React from "react";

import { Opinion, OpinionResult, OpinionVote } from "@/service/Model";
import { scaleRange } from "@/utils/transform";

import MeterOpinion from "./components/MeterOpinion";
import MeterPreview from "./components/MeterPreview";
import MeterResults from "./components/MeterResults";
import MeterTemplate from "./components/MeterTemplate";
import MeterUserIconHandle from "./components/MeterUserIconHandle";
import MeterVoteHandle from "./components/MeterVoteHandle";
import {
  buckets,
  centerPoint,
  getAnswerBucket,
  maxAnswer,
  minAnswer,
  percentageGutter,
  Point,
  Result,
  totalWidth,
} from "./MeterConstants";
import * as MeterConstants from "./MeterConstants";
import { MeterSVG } from "./styles";

type MeterProps = {
  interaction: Opinion;
  interactionResults: OpinionResult;
  previousAnswer?: number;
  icons: IIcons;
  tealiumConfig?: ITealium;
  onAnswer: (answer: number) => void;
};

type MeterState = {
  answer?: number;
  voteHandle: number;
  voteHandleTouched: boolean;
  voteUserIconHandle?: Point;
  dropVoteUserIconHandle: boolean;
  showOutro: boolean;
  showPreview: boolean;
  editorialOpinionIndex: number;
  editorialOpinionOpen: boolean;
};

class MeterAssembler extends React.PureComponent<MeterProps, MeterState> {
  public static defaultProps: any = {
    onAnswer: (answer: number) => answer,
  };
  private svg: SVGSVGElement;
  private cursorPoint: SVGPoint;
  private results: Result[] = [];
  private outroTimeout?: any;

  constructor(props: MeterProps) {
    super(props);
    this.state = {
      voteHandle: centerPoint.x,
      voteHandleTouched: false,
      dropVoteUserIconHandle: false,
      showOutro: false,
      showPreview: true,
      editorialOpinionIndex: -1,
      editorialOpinionOpen: false,
    };
    this.handleOnClick = this.handleOnClick.bind(this);
    this.handleOnMove = this.handleOnMove.bind(this);
    this.handleOnMoveDown = this.handleOnMoveDown.bind(this);
    this.handleOnMouseDown = this.handleOnMouseDown.bind(this);
    this.moveVoteHandle = this.moveVoteHandle.bind(this);
    this.snapVoteHandle = this.snapVoteHandle.bind(this);
    this.handleOpenEditorialOpinion = this.handleOpenEditorialOpinion.bind(
      this
    );
  }

  public UNSAFE_componentWillMount(): void {
    this.calculateResults(this.props.interactionResults.value);
    if (this.props.previousAnswer !== undefined) {
      this.updateAnswer(
        this.props.previousAnswer,
        buckets[getAnswerBucket(this.props.previousAnswer)]
      );
      this.setState({
        voteUserIconHandle: this.getVoteUserIconHandlePosition(
          this.props.previousAnswer
        ),
      });
    }
  }

  public componentDidMount(): void {
    if (this.svg && this.svg.createSVGPoint) {
      this.cursorPoint = this.svg.createSVGPoint();
    }
  }

  public render(): JSX.Element {
    const {
      interaction,
      interactionResults,
      icons,
      tealiumConfig,
    } = this.props;
    const {
      showOutro,
      voteHandle,
      voteHandleTouched,
      voteUserIconHandle,
      dropVoteUserIconHandle,
      answer,
      showPreview,
      editorialOpinionIndex,
      editorialOpinionOpen,
    } = this.state;

    return (
      <MeterTemplate
        results={interactionResults}
        meter={interaction}
        icons={icons}
        answer={answer}
        onConfirmed={() => this.setState({ showPreview: true })}
        onOpinionClose={() => this.handleCloseEditorialOpinion()}
        editorialOpinionIndex={editorialOpinionIndex}
        editorialOpinionOpen={editorialOpinionOpen}
        showOutro={showOutro}
        onOutroClose={() => this.setState({ showOutro: !this.state.showOutro })}
        tealiumConfig={tealiumConfig}
      >
        <MeterSVG
          onClick={this.handleOnClick}
          onMouseUp={this.handleOnClick}
          onMouseMove={this.handleOnMove}
          onMouseDown={this.handleOnMouseDown}
          onMouseLeave={(e: React.MouseEvent) => {
            if (!e.buttons) {
              return;
            }
            this.resetVoteHandle();
          }}
          style={{
            position: "relative",
            margin: "10px",
          }}
          ref={(svg: SVGSVGElement) => (this.svg = svg)}
          viewBox={"0 -95 " + totalWidth + " 470"}
        >
          <MeterResults
            answer={answer}
            results={this.results}
            meter={interaction}
          />
          {showPreview && (
            <MeterPreview answer={answer} results={this.results} />
          )}
          {!MeterConstants.isEnded(interaction) && (
            <MeterVoteHandle
              icons={icons}
              answer={answer}
              voteHandle={voteHandle}
              voteHandleTouched={voteHandleTouched}
              onMoveVoteHandle={this.moveVoteHandle}
              onSnapVoteHandle={this.snapVoteHandle}
            />
          )}
          {!MeterConstants.isEnded(interaction) && (
            <MeterUserIconHandle
              icons={icons}
              voteUserIconHandle={voteUserIconHandle}
              dropVoteUserIconHandle={dropVoteUserIconHandle}
            />
          )}
          {answer !== undefined && (
            <MeterOpinion
              meter={interaction}
              results={this.results}
              onClickOpinion={this.handleOpenEditorialOpinion}
            />
          )}
        </MeterSVG>
      </MeterTemplate>
    );
  }

  private handleOnClick(
    e: React.MouseEvent<SVGSVGElement> & {
      clientX: number;
      clientY: number;
    }
  ): void {
    if (this.state.editorialOpinionIndex >= 0) {
      return;
    }
    e.persist();
    e.nativeEvent.preventDefault();
    if (
      e.target &&
      (e.target as any).parentNode &&
      (e.target as any).parentNode.classList.contains("meterResult")
    ) {
      return;
    }
    this.moveVoteHandle(e.clientX, e.clientY);
    setTimeout(() => this.snapVoteHandle(e.clientX, e.clientY));
    if (this.state.voteHandleTouched) {
      this.setState({ voteHandleTouched: false });
    }
  }

  private handleOnMouseDown(
    e: React.MouseEvent<SVGSVGElement> & {
      clientX: number;
      clientY: number;
    }
  ): void {
    if (this.state.editorialOpinionIndex >= 0) {
      this.handleCloseEditorialOpinion();
    }
    e.persist();
    e.nativeEvent.preventDefault();
    if (
      e.target &&
      (e.target as any).parentNode &&
      (e.target as any).parentNode.classList.contains("meterResult")
    ) {
      return;
    }
    this.moveVoteHandle(e.clientX, e.clientY);
    this.setState({ voteHandleTouched: true });
  }

  private handleOpenEditorialOpinion(index: number): void {
    this.setState({
      editorialOpinionIndex: index,
      editorialOpinionOpen: true,
    });
  }

  private handleCloseEditorialOpinion(): void {
    setTimeout(() => this.setState({ editorialOpinionOpen: false }), 3000);
    this.setState({ editorialOpinionIndex: -1 });
  }

  private handleOnMoveDown(
    e: React.MouseEvent<SVGSVGElement> & {
      clientX: number;
      clientY: number;
    }
  ): void {
    const { interaction } = this.props;
    if (MeterConstants.isEnded(interaction)) {
      return;
    }
    e.persist();
    e.nativeEvent.preventDefault();
    this.moveVoteHandle(e.clientX, e.clientY);
  }

  private handleOnMove(e: React.MouseEvent<SVGSVGElement>): void {
    const { interaction } = this.props;
    if (MeterConstants.isEnded(interaction)) {
      return;
    }
    if (!e.buttons) {
      return;
    }
    e.persist();
    e.nativeEvent.preventDefault();
    this.setState({ voteHandleTouched: true });
    this.moveVoteHandle(e.clientX, e.clientY);
  }

  private moveVoteHandle(clientX: number, clientY: number): void {
    if (this.state.editorialOpinionIndex >= 0) {
      return;
    }
    this.cursorPoint.x = clientX;
    this.cursorPoint.y = clientY;
    const oldAnswer = this.state.answer;
    const voteHandle = this.getSvgCoordinates(this.cursorPoint);

    this.setState({
      voteHandle: voteHandle.x,
    });

    if (oldAnswer === undefined || this.outroTimeout !== undefined) {
      if (this.outroTimeout !== undefined) {
        clearTimeout(this.outroTimeout);
      }

      this.outroTimeout = setTimeout(() => {
        this.setState({ showOutro: true });
        this.outroTimeout = undefined;
      }, 3500);
    }
  }

  private resetVoteHandle(): void {
    const answer = this.state.answer;
    if (answer === undefined) {
      this.setState({
        voteHandle: centerPoint.x,
        voteHandleTouched: false,
      });
      return;
    }

    const voteHandle = buckets[getAnswerBucket(answer)];
    this.setState({
      voteHandle,
      voteHandleTouched: false,
    });
  }

  private getSvgCoordinates(point: SVGPoint): SVGPoint {
    // The cursor point, translated into svg coordinates
    return point.matrixTransform((this.svg as any).getScreenCTM().inverse());
  }

  private getRescaledXCoordinate(point: SVGPoint): number {
    // The zero point is for the buckets is in the middle
    const svgCoordinates = this.getSvgCoordinates(point);
    return svgCoordinates.x - centerPoint.x;
  }

  private snapVoteHandle(clientX: number, clientY: number): void {
    const oldAnswer = this.state.answer;
    const values = this.props.interactionResults.value;
    this.cursorPoint.x = clientX;
    this.cursorPoint.y = clientY;

    if (oldAnswer !== undefined) {
      const oldAnswerBucket = getAnswerBucket(oldAnswer);
      values[oldAnswerBucket] = Math.max(0, values[oldAnswerBucket] - 1);
    }

    const answer = this.getRescaledXCoordinate(this.cursorPoint);
    const answerBucket = getAnswerBucket(answer);
    values[answerBucket] = values[answerBucket] + 1;

    this.calculateResults(values);

    const voteHandle = buckets[getAnswerBucket(answer)];

    this.updateAnswer(answer, voteHandle, this.onAnswer);

    this.setState(
      {
        voteUserIconHandle: {
          x: this.getVoteUserIconHandlePosition(answer).x,
          y: 30,
        },
        dropVoteUserIconHandle: true,
      },
      () => {
        setTimeout(() => {
          this.setState({
            voteUserIconHandle: this.getVoteUserIconHandlePosition(answer),
            dropVoteUserIconHandle: false,
          });
        }, 50);
      }
    );
  }

  private updateAnswer(
    answer: number,
    voteHandle: number,
    callback?: () => void
  ): void {
    this.setState(
      {
        answer,
        voteHandle,
      },
      callback
    );
  }

  private calculateResults(value: number[]): void {
    const results: Result[] = [];
    // TODO: check value against bucket length
    let totalAmount = 0;
    value.forEach((i: number, index: number) => {
      let result = results[index];

      if (!result) {
        result = {
          amount: 0,
          percent: 0,
          bucket: buckets[index],
          opacity: 1,
          size: 5,
        };
        results[index] = result;
      }

      result.amount += i;
      totalAmount += i;
    });

    let maxPercent = MeterConstants.maxPercent;
    let minPercent = MeterConstants.minPercent;

    results.forEach((result: Result) => {
      result.percent =
        totalAmount > 0 ? Math.round((result.amount / totalAmount) * 100) : 0;
      if (result.percent > maxPercent) {
        maxPercent = result.percent;
      }
      if (result.percent < minPercent) {
        minPercent = result.percent;
      }
    });

    results.forEach((result: Result) => {
      if (result.percent > 0) {
        const alpha = scaleRange(
          result.percent,
          minPercent,
          maxPercent,
          MeterConstants.minAlpha,
          MeterConstants.maxAlpha
        );
        const size = scaleRange(
          result.percent,
          minPercent,
          maxPercent,
          MeterConstants.minSize(false),
          MeterConstants.maxSize
        );

        result.opacity = alpha;
        result.size = size;
      } else {
        result.opacity = MeterConstants.minAlpha;
        result.size = MeterConstants.minSize(false);
      }
    });

    this.results = results;
  }

  private onAnswer: () => void = MeterConstants.debounce(() => {
    this.props.onAnswer(this.state.answer!);
  }, 200);

  private getVoteUserIconHandlePosition(answer: number): Point {
    const { interaction } = this.props;
    const answerBucket = getAnswerBucket(answer);
    let bucketOpinionsCount = 0;

    (interaction.opinions || []).forEach((opinion: OpinionVote) => {
      const opinionBucket = getAnswerBucket(
        scaleRange(opinion.opinion, 0, 180, minAnswer, maxAnswer)
      );
      if (opinionBucket === answerBucket) {
        bucketOpinionsCount++;
      }
    });

    const x = buckets[answerBucket];
    const result = this.results[answerBucket];
    const y =
      centerPoint.y -
      (result ? result.size : 0) -
      percentageGutter -
      bucketOpinionsCount * 64;
    return { x, y };
  }
}

export default MeterAssembler;
